import {
    CopyOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EyeOutlined,
    SearchOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import {
    Button,
    Col,
    Form,
    Image,
    Input,
    message,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { videoStatus } from '../interface/listconstant';
import { deleteMinimaxVideo, deleteRunwayVideo } from '../services/api';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    downloadVideos,
    fetchVideos,
    setVideoId,
} from '../store/slice/homeSlice';
import DateFilter from './DateFilter';

const API_URL = process.env.REACT_APP_API_URL;

const VideoTable = ({ provider, reloadKey, previews = [] }) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();

    const {
        videos: data,
        loading,
        error,
        pagination,
    } = useAppSelector((state) => state.video);

    // Ensure data is always an array
    const safeData = Array.isArray(data) ? data : [];

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [allTitles, setAllTitles] = useState([]);
    const [previewVideoURL, setPreviewVideoURL] = useState(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    // State for filters and pagination
    const [filters, setFilters] = useState({ status: 'completed' });
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [reload, setReload] = useState(false);

    const currentUser = JSON.parse(
        localStorage.getItem('account_web') || 'null',
    );

    // Main fetch videos effect
    useEffect(() => {
        if (currentUser?.id) {
            const queryOptions = {
                userId: currentUser.id,
                page: currentPage,
                limit: pageSize,
                filters,
            };

            dispatch(fetchVideos(queryOptions));
        }
    }, [
        dispatch,
        reload,
        provider,
        currentUser?.id,
        filters,
        currentPage,
        pageSize,
        reloadKey,
    ]);

    // Handle form submission (filter)
    const onFinish = useCallback((values) => {
        const newFilters = {};

        // Only add non-empty values to filters
        if (values.searchText?.trim()) {
            newFilters.searchText = values.searchText.trim();
        }

        if (values.selectedTitleFilter) {
            newFilters.title = values.selectedTitleFilter;
        }

        if (values.selectedVideoStatus) {
            newFilters.status = values.selectedVideoStatus;
        }

        if (values.dateRange && values.dateRange.length === 2) {
            newFilters.startDate = values.dateRange[0].toISOString();
            newFilters.endDate = values.dateRange[1].toISOString();
        }

        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page when filtering
    }, []);

    // Handle form reset
    const onReset = useCallback(() => {
        form.resetFields();
        setFilters({}); // Clear all filters
        setCurrentPage(1);
    }, [form]);

    // Handle pagination change
    const handleTableChange = useCallback((paginationInfo) => {
        setCurrentPage(paginationInfo.current);
        setPageSize(paginationInfo.pageSize);
        // useEffect will trigger automatically with new pagination values
    }, []);

    // Error handling
    useEffect(() => {
        if (error) {
            message.error('Lỗi khi tải danh sách video.');
        }
    }, [error]);

    // Load titles for filter dropdown
    useEffect(() => {
        if (currentUser?.id) {
            fetch(`${API_URL}/prompts/titles?accountId=${currentUser.id}`)
                .then((res) => res.json())
                .then((data) => {
                    if (Array.isArray(data)) {
                        const uniqueTitles = Array.from(new Set(data));
                        setAllTitles(uniqueTitles);
                    }
                })
                .catch((err) => console.error('Lỗi khi load titles:', err));
        }
    }, [reloadKey]);

    // Debug previews
    useEffect(() => {
        console.log('🖼️ Previews nhận được:', previews);
    }, [previews]);

    useEffect(() => {
        const stored = localStorage.getItem('downloaded_videos');
        if (!stored) return;

        let ids = [];
        try {
            ids = JSON.parse(stored);
        } catch (err) {
            console.error(
                '❌ Lỗi parse downloaded_videos từ localStorage:',
                err,
            );
            return;
        }

        if (!Array.isArray(ids) || ids.length === 0) return;

        // Lọc ra các id chưa được đánh dấu là downloaded trong safeData
        const notYetDownloaded = ids.filter(
            (id) =>
                !safeData.find((item) => item.id === id && item.isDownloaded),
        );

        if (notYetDownloaded.length === 0) {
            // Tất cả đã sync rồi, xoá localStorage luôn
            localStorage.removeItem('downloaded_videos');
            return;
        }

        dispatch(downloadVideos({ ids: notYetDownloaded, isDownloaded: true }))
            .then(() => {
                // Xoá những id đã sync ra khỏi localStorage
                const remaining = ids.filter(
                    (id) => !notYetDownloaded.includes(id),
                );
                if (remaining.length > 0) {
                    localStorage.setItem(
                        'downloaded_videos',
                        JSON.stringify(remaining),
                    );
                } else {
                    localStorage.removeItem('downloaded_videos');
                }
            })
            .catch((err) =>
                console.error('❌ Lỗi đồng bộ trạng thái tải:', err),
            );
    }, [dispatch, safeData]);

    // Delete multiple videos
    const handleDeleteVideos = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning('Chưa chọn video nào!');
            return;
        }
        let success = 0;
        let failed = 0;

        for (const id of selectedRowKeys) {
            try {
                if (id) {
                    await deleteMinimaxVideo(id);
                    success++;
                } else {
                    failed++;
                }
            } catch (err) {
                message.error(`Lỗi khi xoá video ${id}: ${err.message}`);
                failed++;
            }
        }

        message.success(`Đã xoá thành công ${success} video`);
        setReload((state) => !state);
        setSelectedRowKeys([]);
    };

    // Delete single video
    const handleDeleteOneVideo = async (id) => {
        try {
            if (!id) {
                message.error(`Không tìm thấy Video ${id}`);
                return;
            }
            await deleteMinimaxVideo(id);
        } catch (err) {
            console.error('Xoá thất bại:', err);
            message.error(`Lỗi khi xoá video ${id}: ${err.message}`);
        }

        message.success(`Đã xoá thành công video`);
        setReload((state) => !state);
    };

    // Row selection for table
    const rowSelection = {
        selectedRowKeys,
        onChange: (newKeys) => setSelectedRowKeys(newKeys),
    };

    // Sequential download function
    // Sequential download function - FIXED VERSION
    const downloadSequentially = async (videos, delay = 1000) => {
        const downloadedIds = [];
        let successCount = 0;
        let failedCount = 0;

        for (let i = 0; i < videos.length; i++) {
            const item = videos[i];
            const url = item.videoURL || item.video;

            // Kiểm tra URL hợp lệ - FIX: thêm kiểm tra null value và trim()
            const isValidUrl =
                url &&
                url !== '#' &&
                url !== 'null' &&
                url !== null &&
                url.toString().trim() !== '';

            if (isValidUrl) {
                try {
                    // Thực hiện tải
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `video_${item.id || item.key}.mp4`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    // CHỈ lưu ID vào mảng KHI download thành công
                    downloadedIds.push(item.id);
                    successCount++;

                    console.log(
                        `✅ Đã tải video ID: ${item.id} - (${successCount}/${videos.length})`,
                    );

                    // Delay giữa các lần tải
                    if (i < videos.length - 1) {
                        // Không delay cho video cuối cùng
                        await new Promise((resolve) =>
                            setTimeout(resolve, delay),
                        );
                    }
                } catch (error) {
                    console.error(`❌ Lỗi tải video ID: ${item.id}`, error);
                    failedCount++;
                }
            } else {
                console.warn(
                    `⚠️ Bỏ qua video ID: ${item.id} - URL không hợp lệ:`,
                    url,
                );
                failedCount++;
            }
        }

        // CHỈ gọi API update khi có video được tải thành công
        if (downloadedIds.length > 0) {
            try {
                await dispatch(
                    downloadVideos({ ids: downloadedIds, isDownloaded: true }),
                ).unwrap();

                message.success(
                    `Đã tải thành công ${successCount} video${
                        successCount > 1 ? 's' : ''
                    }!`,
                );

                setReload((state) => !state);
                setSelectedRowKeys([]);
            } catch (err) {
                console.error('❌ Lỗi lưu trạng thái tải hàng loạt', err);
                message.error(
                    'Đã tải file nhưng có lỗi khi cập nhật trạng thái!',
                );
            }
        } else {
            message.warning('Không có video nào được tải do URL không hợp lệ!');
            console.warn('⚠️ Không có video nào được tải!');
        }

        // Hiển thị thông báo tổng kết nếu có video thất bại
        if (failedCount > 0) {
            message.warning(
                `${failedCount} video không thể tải do URL không hợp lệ!`,
            );
        }
    };

    // Table columns definition
    const columns = [
        {
            title: 'Hành động',
            fixed: 'left',
            width: 130,
            align: 'center',
            render: (_, record) => {
                const url = record.videoURL || record.video;
                const isCompleted = record.status === 'completed';

                if (!isCompleted || !url || url === '#' || url === 'null') {
                    return (
                        <Tooltip title="Xoá" placement="bottom">
                            <Popconfirm
                                title="Xoá video"
                                description="Bạn có chắc chắn muốn xoá video này?"
                                onConfirm={() =>
                                    handleDeleteOneVideo(record.id)
                                }
                                okText="Đồng ý"
                                cancelText="Hủy"
                            >
                                <Button icon={<DeleteOutlined />} danger />
                            </Popconfirm>
                        </Tooltip>
                    );
                }

                const handleDownload = async (record) => {
                    // Gọi API lưu trạng thái tải
                    try {
                        await dispatch(
                            downloadVideos({
                                ids: [record.id],
                                isDownloaded: true,
                            }),
                        ).unwrap();
                        setReload((state) => !state);
                    } catch (err) {
                        console.error(
                            `❌ Lỗi lưu trạng thái tải video ${record.id}`,
                            err,
                        );
                    }
                };

                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 4,
                        }}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, 1fr)',
                                gap: 4,
                            }}
                        >
                            <Tooltip title="Xem video">
                                <Button
                                    icon={<EyeOutlined />}
                                    onClick={() => {
                                        setIsPreviewOpen(false);
                                        setPreviewVideoURL(null);
                                        setTimeout(() => {
                                            setPreviewVideoURL(url);
                                            setIsPreviewOpen(true);
                                        }, 100);
                                    }}
                                />
                            </Tooltip>

                            <Tooltip title="Tải xuống">
                                <a
                                    href={url}
                                    download
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={() => handleDownload(record)}
                                >
                                    <Button icon={<DownloadOutlined />} />
                                </a>
                            </Tooltip>

                            <Tooltip title="Tạo lại" placement="bottom">
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={() =>
                                        dispatch(setVideoId(record.id))
                                    }
                                />
                            </Tooltip>

                            <Tooltip title="Xoá" placement="bottom">
                                <Popconfirm
                                    title="Xoá video"
                                    description="Bạn có chắc chắn muốn xoá video này?"
                                    onConfirm={() =>
                                        handleDeleteOneVideo(record.id)
                                    }
                                    okText="Đồng ý"
                                    cancelText="Hủy"
                                >
                                    <Button icon={<DeleteOutlined />} danger />
                                </Popconfirm>
                            </Tooltip>
                        </div>

                        {record.isDownloaded && (
                            <Tag color="green" style={{ fontSize: 10 }}>
                                Đã tải
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            render: (date) => (date ? dayjs(date).format('DD/MM/YYYY') : 'N/A'),
        },
        {
            title: 'Ngày cập nhật',
            dataIndex: 'updatedAt',
            render: (_, record) => {
                const date = record.updatedAt;
                const url = record.videoURL || record.video;
                const isCompleted = record.status === 'completed';
                if (!isCompleted || !url || url === '#' || url === 'null') {
                    return 'N/A';
                }

                return date ? dayjs(date).format('DD/MM/YYYY HH:mm:ss') : 'N/A';
            },
        },
        {
            title: 'Ngày cập nhật',
            dataIndex: 'updatedAt',
            render: (_, record) => {
                const date = record.updateAt;
                const url = record.videoURL || record.video;
                const isCompleted = record.status === 'completed';
                if (!isCompleted || !url || url === '#' || url === 'null') {
                    return 'N/A';
                }

                return date ? date.format('DD/MM/YYYY HH:mm:ss') : 'N/A';
            },
        },
        {
            title: 'Ảnh',
            dataIndex: 'thumbnail',
            render: (_, record) => {
                const thumbnail = record.thumbnail?.toString().trim();
                const imageBase64 = record.imageBase64?.toString().trim();

                const isValidThumb =
                    thumbnail && thumbnail !== 'null' && thumbnail !== '';

                const src = isValidThumb ? thumbnail : imageBase64;

                return src ? (
                    <Image width={180} src={src} />
                ) : (
                    <span style={{ color: 'gray' }}>Đang xử lý...</span>
                );
            },
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            render: (status) => {
                const matchedStatus = videoStatus.find((item) =>
                    Array.isArray(item.status)
                        ? item.status.includes(status)
                        : item.status === status,
                );

                const color = matchedStatus?.color || 'default';
                const label = matchedStatus?.label || 'Không xác định';

                return <Tag color={color}>{label}</Tag>;
            },
        },
        {
            title: 'Mô tả',
            dataIndex: 'description',
            render: (text) => (
                <div
                    style={{
                        height: 150,
                        width: 400,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 6,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {text}
                </div>
            ),
        },
        { title: 'Chủ đề', dataIndex: 'title' },
        { title: 'AI', dataIndex: 'ai' },
    ];

    return (
        <div>
            <h1>📋 Danh sách video đã tạo</h1>

            {/* Filter Form */}
            <Form
                form={form}
                onFinish={onFinish}
                layout="vertical"
                style={{
                    marginBottom: 16,
                    padding: 16,
                    background: '#fafafa',
                    borderRadius: 8,
                }}
                initialValues={{
                    selectedVideoStatus: 'completed',
                }}
            >
                <Row gutter={16}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Tìm kiếm video" name="searchText">
                            <Input
                                placeholder="Tìm kiếm trong mô tả..."
                                allowClear
                                prefix={<SearchOutlined />}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="Lọc theo chủ đề"
                            name="selectedTitleFilter"
                        >
                            <Select placeholder="Chọn chủ đề" allowClear>
                                {allTitles.map((title, index) => (
                                    <Select.Option key={index} value={title}>
                                        {title}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="Lọc theo trạng thái"
                            name="selectedVideoStatus"
                        >
                            <Select placeholder="Chọn trạng thái" allowClear>
                                {videoStatus.map((item) => (
                                    <Select.Option
                                        key={item.id}
                                        value={item.status}
                                    >
                                        {item.label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item label="Khoảng thời gian" name="dateRange">
                            <DateFilter />
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Col span={24}>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SearchOutlined />}
                                loading={loading}
                            >
                                Lọc
                            </Button>
                            <Button
                                onClick={onReset}
                                icon={<UndoOutlined />}
                                disabled={loading}
                            >
                                Đặt lại
                            </Button>
                            <Button
                                onClick={() => window.location.reload()}
                                disabled={loading}
                            >
                                Làm mới
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>

            {/* Action Buttons */}
            <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                {['minimax', 'runway'].includes(provider) && (
                    <>
                        <Popconfirm
                            title="Bạn có chắc muốn xoá các video đã chọn?"
                            onConfirm={handleDeleteVideos}
                            okText="Xoá"
                            cancelText="Huỷ"
                            disabled={selectedRowKeys.length === 0}
                        >
                            <Button
                                danger
                                disabled={selectedRowKeys.length === 0}
                            >
                                Xoá
                                {selectedRowKeys.length > 0
                                    ? ` (${selectedRowKeys.length})`
                                    : ''}
                            </Button>
                        </Popconfirm>

                        <Button
                            type="primary"
                            disabled={selectedRowKeys.length === 0}
                            onClick={() => {
                                const selectedVideos = safeData.filter((item) =>
                                    selectedRowKeys.includes(item.id),
                                );
                                downloadSequentially(selectedVideos);
                            }}
                        >
                            Tải tất cả ({selectedRowKeys.length})
                        </Button>
                    </>
                )}
            </Space>

            <Table
                columns={columns}
                dataSource={safeData}
                loading={loading}
                rowSelection={rowSelection}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: pagination?.total || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) =>
                        `${range[0]}-${range[1]} của ${total} videos`,
                    pageSizeOptions: ['10', '20', '50', '100'],
                }}
                onChange={handleTableChange}
            />

            {/* Preview Modal */}
            <Modal
                title="Xem video"
                open={isPreviewOpen}
                footer={null}
                onCancel={() => setIsPreviewOpen(false)}
                width={800}
            >
                {previewVideoURL && (
                    <video
                        key={previewVideoURL}
                        width="100%"
                        height="auto"
                        controls
                        autoPlay
                    >
                        <source src={previewVideoURL} type="video/mp4" />
                        Trình duyệt không hỗ trợ xem video.
                    </video>
                )}
            </Modal>
        </div>
    );
};

export default VideoTable;
